import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { Search, Filter, ChevronDown } from "lucide-react";
import ToggleLeft from "./images/toggle-button-left.png";
import ToggleRight from "./images/toggle-button-right.png";

import "./DataDisplay.css";
import BCard from "./BCard";
import BrandsHeader from "./BrandsHeader";
import { getBrandInsightsAndMetrics } from "../../services/dataAnalyticsApi";
import AllBrandsPageSkeleton from "./Skeletons/AllBrandsPageSkeleton";

const DataDisplay = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState(null);
  const [showZeroData, setShowZeroData] = useState(false);
  const [sortDirection, setSortDirection] = useState("asc");

  const [brandsData, setBrandsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedSectors, setSelectedSectors] = useState([]);
  const [isSectorDropdownOpen, setSectorDropdownOpen] = useState(false);
  const sectorFilterRef = useRef(null);

  const fetchBrandData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getBrandInsightsAndMetrics();
      if (
        response.insights &&
        response.insights.success &&
        response.insights.data &&
        response.metrics &&
        response.metrics.success &&
        response.metrics.data
      ) {
        const brandInsights = response.insights.data;
        const brandMetrics = response.metrics.data;

        const combinedBrands = brandInsights.map((insightBrand) => {
          const correspondingMetrics = brandMetrics.find(
            (metricBrand) => metricBrand.brandId === insightBrand.brandId
          );

          return {
            id: String(insightBrand.brandId),
            name: insightBrand.brandName,
            friendlyName: insightBrand.brandFriendlyName,
            originalInsertedDate: insightBrand.insertedDate,
            insertedDate: new Date(
              insightBrand.insertedDate
            ).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "2-digit",
            }),
            sector: insightBrand.sectorName,
            subsector: insightBrand.subSectorName,
            sectorId: String(insightBrand.sectorId),
            channels: insightBrand.urls,
            metrics: correspondingMetrics || {
              trendPercentage: 0,
              totalMentions: 0,
              brandPosts: 0,
              brandReplies: 0,
              userComments: 0,
              totalMentionsFromBeginning: 0,
              brandPostsFromBeginning: 0,
              brandCommentsFromBeginning: 0,
              userCommentsFromBeginning: 0,
            },
          };
        });

        setBrandsData(combinedBrands);
      } else {
        console.warn("API call returned incomplete data:", response);
        setError("Failed to retrieve complete brand data.");
      }
    } catch (err) {
      console.error("Error fetching brand data:", err);
      setError("Failed to load brand data. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBrandData();
  }, [fetchBrandData]);

  const uniqueSectors = useMemo(() => {
    const sectors = new Set(brandsData.map((brand) => brand.sector));
    return Array.from(sectors).sort();
  }, [brandsData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sectorFilterRef.current &&
        !sectorFilterRef.current.contains(event.target)
      ) {
        setSectorDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredAndSortedBrands = useMemo(() => {
    let filtered = brandsData.filter((brand) => {
      const matchesSearch =
        brand.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        brand.friendlyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        brand.sector.toLowerCase().includes(searchTerm.toLowerCase()) ||
        brand.subsector.toLowerCase().includes(searchTerm.toLowerCase());

      const hasData = showZeroData
        ? brand.metrics.totalMentions === 0 &&
          brand.metrics.brandComments === 0 &&
          brand.metrics.brandPosts === 0 &&
          brand.metrics.userComments === 0
        : true;

      // const hasData = showZeroData
      //   ? brand.metrics.totalMentionsFromBeginning === 0 &&
      //     brand.metrics.brandCommentsFromBeginning === 0 &&
      //     brand.metrics.brandPostsFromBeginning === 0 &&
      //     brand.metrics.userCommentsFromBeginning === 0
      //   : true;

      const matchesSector =
        selectedSectors.length === 0 || selectedSectors.includes(brand.sector);

      return matchesSearch && hasData && matchesSector;
    });

    filtered.sort((a, b) => {
      const direction = sortDirection === "asc" ? 1 : -1;

      if (!sortBy) return direction * (parseInt(a.id) - parseInt(b.id));
      if (sortBy === "name") return direction * a.name.localeCompare(b.name);
      if (sortBy === "date")
        return (
          direction *
          (new Date(a.originalInsertedDate) - new Date(b.originalInsertedDate))
        );
      if (sortBy === "sector")
        return direction * a.sector.localeCompare(b.sector);
      if (sortBy === "totalMentions")
        return direction * (a.metrics.totalMentions - b.metrics.totalMentions);

      return 0;
    });

    return filtered;
  }, [
    brandsData,
    searchTerm,
    sortBy,
    sortDirection,
    showZeroData,
    selectedSectors,
  ]);

  const handleSectorChange = (sector) => {
    setSelectedSectors((prevSelected) => {
      if (prevSelected.includes(sector)) {
        return prevSelected.filter((s) => s !== sector);
      } else {
        return [...prevSelected, sector];
      }
    });
  };

  if (isLoading) {
    return (
      <div className="page">
        <div className="loading-state">
          {/* <h3>Loading Brands...</h3> */}
          <AllBrandsPageSkeleton />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="error-state">
          <h3>Error: {error}</h3>
          <button onClick={fetchBrandData}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container">
        <BrandsHeader />
        <section className="controls">
          <div className="search-box">
            <Search className="icon" />
            <input
              type="text"
              placeholder="Search brands, sectors, or subsectors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="sector-filter-container" ref={sectorFilterRef}>
            <button
              className="sector-dropdown-button"
              onClick={() => setSectorDropdownOpen(!isSectorDropdownOpen)}
            >
              <span>
                {selectedSectors.length > 0
                  ? `${selectedSectors.length} Sector(s) Selected`
                  : "Filter by Sector"}
              </span>
              <ChevronDown size={20} />
            </button>
            {isSectorDropdownOpen && (
              <div className="sector-dropdown-menu">
                {uniqueSectors.map((sector) => (
                  <label key={sector} className="sector-dropdown-item">
                    <input
                      type="checkbox"
                      checked={selectedSectors.includes(sector)}
                      onChange={() => handleSectorChange(sector)}
                    />
                    <span title={sector}>{sector}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="sort-toggle">
            <select
              value={sortBy || ""}
              onChange={(e) => {
                setSortBy(e.target.value || null);
                setSortDirection("asc");
              }}
              className="select"
            >
              <option value="">Brand ID</option>
              <option value="name">Brand Name</option>
              <option value="date">Inserted Date</option>
              <option value="sector">Sector</option>
              <option value="totalMentions">Total Mentions</option>
            </select>

            <button
              className="toggle-btn"
              onClick={() =>
                setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))
              }
              title={`Sort ${
                sortDirection === "asc" ? "Descending" : "Ascending"
              }`}
            >
              {sortDirection === "asc" ? "Sort ⬇️" : "Sort ⬆️"}
            </button>

            <div className="toggle">
              <span>Show zero data</span>
              <button
                onClick={() => setShowZeroData(!showZeroData)}
                className={`toggle-btn ${showZeroData ? "active" : ""}`}
              >
                {showZeroData ? (
                  <img className="toggle-button" src={ToggleRight} />
                ) : (
                  <img className="toggle-button" src={ToggleLeft} />
                )}
              </button>
            </div>
          </div>
        </section>

        <div className="results-info">
          <strong>Showing:</strong> {filteredAndSortedBrands.length} of{" "}
          {brandsData.length} Brands
        </div>

        <div className="brand-grid">
          {filteredAndSortedBrands.map((brand) => (
            <BCard key={brand.id} brand={brand} />
          ))}
        </div>

        {filteredAndSortedBrands.length === 0 && (
          <div className="empty-state">
            <Filter className="icon-lg" />
            <h3>No brands found</h3>
            <p>Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataDisplay;
